import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  Target,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Zap,
  ArrowRight,
  Play,
  CheckCircle,
  AlertTriangle,
  PieChart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(value);
};

export const Optimize = () => {
  const { isPaperTrading } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [rebalancing, setRebalancing] = useState(false);
  const [optimization, setOptimization] = useState(null);
  const [rebalanceHistory, setRebalanceHistory] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    fetchOptimization();
    fetchRebalanceHistory();
  }, []);

  const fetchOptimization = async () => {
    setOptimizing(true);
    try {
      const response = await axios.get(`${API}/optimize`);
      setOptimization(response.data);
    } catch (error) {
      console.error("Error fetching optimization:", error);
      toast.error("Failed to calculate optimal allocation");
    } finally {
      setLoading(false);
      setOptimizing(false);
    }
  };

  const fetchRebalanceHistory = async () => {
    try {
      const response = await axios.get(`${API}/rebalance/history`);
      setRebalanceHistory(response.data.history || []);
    } catch (error) {
      console.error("Error fetching rebalance history:", error);
    }
  };

  const executeRebalance = async () => {
    setRebalancing(true);
    try {
      const response = await axios.post(`${API}/rebalance`);
      toast.success("Portfolio rebalanced successfully!");
      setConfirmDialogOpen(false);
      fetchOptimization();
      fetchRebalanceHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rebalance portfolio");
    } finally {
      setRebalancing(false);
    }
  };

  // Prepare chart data
  const chartData = optimization?.allocations
    ? Object.entries(optimization.allocations)
        .filter(([_, weight]) => weight > 0.01)
        .map(([symbol, weight]) => ({
          name: symbol,
          value: Math.round(weight * 100),
        }))
    : [];

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="optimize-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="optimize-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Portfolio Optimization
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Black-Litterman model with AI-enhanced views • {isPaperTrading ? "Paper Trading" : "Live Trading"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchOptimization}
            disabled={optimizing}
            data-testid="recalculate-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${optimizing ? 'animate-spin' : ''}`} />
            Recalculate
          </Button>
          <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                size="sm" 
                disabled={!optimization?.rebalance_actions?.length}
                data-testid="rebalance-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Auto-Rebalance
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Auto-Rebalance</DialogTitle>
                <DialogDescription>
                  {isPaperTrading ? (
                    <span className="flex items-center gap-1 text-warning">
                      <AlertTriangle className="w-4 h-4" />
                      Paper Trading Mode - No real money involved
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-destructive">
                      <AlertTriangle className="w-4 h-4" />
                      This will execute real trades with your funds
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm mb-4">
                  This will execute {optimization?.rebalance_actions?.length || 0} trades to optimize your portfolio:
                </p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {optimization?.rebalance_actions?.map((action, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-md text-sm">
                      <span className="font-medium">{action.symbol}</span>
                      <Badge className={action.action === "BUY" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"}>
                        {action.action} {action.shares_to_trade} shares
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={executeRebalance} 
                  disabled={rebalancing}
                  data-testid="confirm-rebalance-btn"
                >
                  {rebalancing ? "Executing..." : "Execute Rebalance"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover gradient-glow" data-testid="expected-return-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Expected Annual Return
              </p>
              <TrendingUp className="w-5 h-5 text-success" />
            </div>
            <p className="text-3xl font-bold text-success" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {optimization?.expected_return?.toFixed(2) || 0}%
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Based on Black-Litterman model
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="volatility-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Expected Volatility
              </p>
              <AlertTriangle className="w-5 h-5 text-warning" />
            </div>
            <p className="text-3xl font-bold text-warning" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {optimization?.expected_volatility?.toFixed(2) || 0}%
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Annualized standard deviation
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="sharpe-ratio-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Sharpe Ratio
              </p>
              <Target className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {optimization?.sharpe_ratio?.toFixed(2) || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Risk-adjusted return (higher is better)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Allocation Chart & Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Optimal Allocation Pie Chart */}
        <Card data-testid="allocation-chart-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-primary" />
              <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Optimal Allocation
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#0A0A0A',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [`${value}%`, 'Weight']}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Rebalance Actions */}
        <Card data-testid="rebalance-actions-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Recommended Actions
                </CardTitle>
              </div>
              <Badge variant="outline">
                {optimization?.rebalance_actions?.length || 0} trades
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {!optimization?.rebalance_actions?.length ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-success" />
                <p>Portfolio is optimally balanced</p>
                <p className="text-xs mt-1">No rebalancing needed</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[280px] overflow-y-auto">
                {optimization.rebalance_actions.map((action, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors"
                    data-testid={`action-${action.symbol}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{action.symbol}</span>
                        <Badge 
                          className={action.action === "BUY" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"}
                        >
                          {action.action}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {action.current_weight}% <ArrowRight className="w-3 h-3 inline mx-1" /> {action.target_weight}%
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium tabular-nums">
                        {action.shares_to_trade} shares
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ~{formatCurrency(action.estimated_value)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rebalance History */}
      {rebalanceHistory.length > 0 && (
        <Card data-testid="rebalance-history-card">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Rebalance History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rebalanceHistory.slice(0, 5).map((event, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-md bg-muted/30"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {new Date(event.timestamp).toLocaleDateString()} at {new Date(event.timestamp).toLocaleTimeString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {event.trades?.length || 0} trades executed
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={event.is_paper ? "text-warning border-warning/30" : "text-success border-success/30"}>
                      {event.is_paper ? "Paper" : "Live"}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Expected: {event.expected_return?.toFixed(1)}% return
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Target className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <h4 className="font-medium text-sm">About Black-Litterman Optimization</h4>
              <p className="text-sm text-muted-foreground mt-1">
                This model combines market equilibrium returns with AI-generated views about specific assets.
                It produces more stable allocations than traditional mean-variance optimization by incorporating
                confidence levels in each view. The AI confidence scores from our analysis directly influence
                the expected returns used in the optimization.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Optimize;
